// Money → Overview (spec 044 D100–D112, plan slice 9): Figma "Financial · Overview"
// (11355:26773) with our money — the month's cards, the 12-month revenue chart, Sessions in and
// out, the month's newest ledger rows and the tutors owed most. The reads are
// src/services/financial.js; the arithmetic is src/utils/financialOverview.js.
import React, { useEffect, useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import { RiArrowDownSLine, RiCalendarLine, RiDownload2Line } from "@remixicon/react";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { getFinancialMonth, getPayoutsDue, getRevenueDays } from "../../services/financial";
import { ListHead } from "../../components/kit/PageHead";
import { ErrorState } from "../../components/kit/States";
import { headButtonSx } from "../../utils/figmaTokens";
import { ledgerRowView } from "../../utils/transactionPresentation";
import { cycleTeaching } from "../../utils/cyclePay";
import {
  monthLabel,
  monthOptions,
  monthWindows,
  moveRows,
  overviewCards,
  overviewCsv,
  overviewNumbers,
  payoutRows,
  revenueBars,
  revenueLine,
  cycleEndLabel,
  thisMonthKey,
} from "../../utils/financialOverview";
import { MovesCard, PayoutsCard, RecentCard, RevenueCard, STAT_TILES, StatTile } from "./Panels";

const Financial = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonthKey());
  const [attempt, setAttempt] = useState(0);
  const [monthAnchor, setMonthAnchor] = useState(null);
  const [data, setData] = useState(null); // { windows, raw } for `month`, or null while reading
  const [chart, setChart] = useState(null); // { days, audience } | { failed: true } | null
  const [due, setDue] = useState(null); // getPayoutsDue's answer | { failed: true } | null

  useEffect(() => {
    let live = true;
    const windows = monthWindows(month, new Date());
    setData(null);
    setChart(null);
    getFinancialMonth(windows)
      .then((raw) => {
        if (live) setData({ windows, raw });
      })
      .catch((e) => {
        console.error("Financial: the month failed:", e);
        if (live) setData({ windows, raw: {} });
      });
    getRevenueDays(month, new Date())
      .then((s) => {
        if (live) setChart(s);
      })
      .catch((e) => {
        console.error("Financial: the summaries failed:", e);
        if (live) setChart({ failed: true });
      });
    return () => {
      live = false;
    };
  }, [month, attempt]);

  useEffect(() => {
    let live = true;
    setDue(null);
    getPayoutsDue()
      .then((d) => {
        if (live) setDue(d);
      })
      .catch((e) => {
        console.error("Financial: payouts due failed:", e);
        if (live) setDue({ failed: true });
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const windows = data ? data.windows : monthWindows(month, new Date());
  const tutors = due && !due.failed ? due.tutors : null;
  const numbers = overviewNumbers({ ...(data ? data.raw : {}), tutors }, windows);
  const cards = overviewCards(numbers, windows, due && !due.failed ? due.cycle : null);
  const bars = chart && chart.audience ? revenueBars(chart.days, month) : [];
  const chartState = !chart ? "loading" : chart.failed ? "failed" : chart.audience ? "ready" : "empty";
  const line = revenueLine(numbers.gross, windows);
  const teaching = due && !due.failed ? cycleTeaching(due.bookings, Object.fromEntries(due.tutors.map((t) => [t.id, t]))) : {};
  const rows = moveRows(numbers.moves);
  const paRows = payoutRows(tutors, teaching);
  const recentViews = data?.raw.recent ? data.raw.recent.rows.map((r) => ledgerRowView(r, data.raw.recent.people)) : [];

  const statsLoading = !data || !due;
  const movesLoading = !data;
  const recentState = !data ? "loading" : data.raw.recent ? "ready" : "failed";
  const payoutsState = !due ? "loading" : due.failed ? "failed" : "ready";
  const cycleLabel = due && !due.failed ? cycleEndLabel(due.cycle) : null;

  const canOpenPeople = hasPermission(user, permissions.VIEW_STUDENTS);
  const canOpenTutors = hasPermission(user, permissions.VIEW_TUTORS);

  const retry = () => setAttempt((n) => n + 1);

  const doExport = () => {
    const { text, filename } = overviewCsv(numbers, bars, windows, new Date());
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <ListHead caption="Money" title="Financial overview" subtitle={`Money in, money out and what is still owed · ${monthLabel(month)}`} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Button
            id="financial-month-button"
            onClick={(e) => setMonthAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(monthAnchor)}
            aria-controls={monthAnchor ? "financial-month-menu" : undefined}
            disableRipple
            disableFocusRipple
            sx={headButtonSx}
          >
            <RiCalendarLine size={18} />
            {monthLabel(month)}
            <RiArrowDownSLine size={16} />
          </Button>
          <Menu
            id="financial-month-menu"
            anchorEl={monthAnchor}
            open={Boolean(monthAnchor)}
            onClose={() => setMonthAnchor(null)}
            slotProps={{ list: { "aria-labelledby": "financial-month-button" } }}
          >
            {monthOptions(new Date()).map((m) => (
              <MenuItem
                key={m.key}
                selected={m.key === month}
                onClick={() => {
                  setMonthAnchor(null);
                  setMonth(m.key);
                }}
              >
                {m.label}
              </MenuItem>
            ))}
          </Menu>
          <Button onClick={doExport} disabled={!data || !due || !chart} disableRipple disableFocusRipple sx={headButtonSx}>
            <RiDownload2Line size={18} />
            Export
          </Button>
        </Box>
      </Box>

      {data && due && (numbers.failed || due.failed) && (
        <ErrorState
          compact
          title="Some numbers could not load"
          message="Numbers that could not be read show —. Try again in a moment."
          onRetry={retry}
        />
      )}

      <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" } }}>
        {STAT_TILES.map((t, i) => (
          <StatTile
            key={t.key}
            Icon={t.Icon}
            tone={t.tone}
            label={cards[i].label}
            value={cards[i].value}
            pill={cards[i].pill}
            loading={statsLoading}
          />
        ))}
      </Box>

      <Box sx={{ display: "grid", gap: "20px", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 400px" }, alignItems: "start" }}>
        <RevenueCard state={chartState} bars={bars} line={line} current={windows.current} onRetry={retry} />
        <MovesCard rows={rows} loading={movesLoading} />
      </Box>

      <Box sx={{ display: "grid", gap: "20px", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 400px" }, alignItems: "start" }}>
        <RecentCard state={recentState} views={recentViews} monthName={monthLabel(month).split(" ")[0]} canOpen={canOpenPeople} onRetry={retry} />
        <PayoutsCard state={payoutsState} rows={paRows} cycleLabel={cycleLabel} canOpen={canOpenTutors} onRetry={retry} />
      </Box>
    </Box>
  );
};

export default Financial;
